import React, { useState, useEffect, useMemo, useRef } from "react";
import { formatCurrency } from "../utils/format";
import { ChevronRightIcon } from "./icons";
import { GROUPS, GROUP_ORDER, getAccountGroupKey, getAccountDisplayName } from "../utils/accountGroups";

const sumCosts = (accounts) =>
  accounts.reduce((sum, a) => sum + (a.cost === null ? 0 : parseFloat(a.cost)), 0);

const SkeletonGroup = () => (
  <div className="border border-slate-100 rounded-xl p-4">
    <div className="h-4 bg-slate-100 rounded animate-pulse w-40" />
  </div>
);

const ChevronToggle = ({ open }) => (
  <ChevronRightIcon className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
);

const AccountRow = ({ account, onSelectAccount }) => {
  const cost = account.cost === null ? null : parseFloat(account.cost);

  return (
    <button
      type="button"
      onClick={() => onSelectAccount({ id: account.accountId, name: account.name })}
      className="w-full flex items-center justify-between gap-3 py-2.5 pl-4 pr-3 rounded-lg hover:bg-brand-50 transition-colors text-left"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{getAccountDisplayName(account)}</p>
        <p className="text-xs text-slate-400 font-mono truncate">{account.name} · {account.accountId}</p>
      </div>
      <span className={cost === null ? "text-slate-400 italic text-sm shrink-0" : "text-slate-900 font-semibold text-sm tabular-nums shrink-0"}>
        {cost === null ? "Unavailable" : formatCurrency(cost)}
      </span>
    </button>
  );
};

const EnvironmentBucket = ({ label, accounts, path, openKeys, toggle, onSelectAccount }) => {
  if (!accounts.length) return null;

  const isOpen = openKeys.has(path);
  const total = sumCosts(accounts);

  return (
    <div className="ml-2 border-l-2 border-slate-100 pl-3">
      <button
        type="button"
        onClick={() => toggle(path)}
        title={`${label} — total for selected range: ${formatCurrency(total)}`}
        className="w-full flex items-center justify-between gap-2 py-2 text-left"
      >
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600">
          <ChevronToggle open={isOpen} />
          {label}
          <span className="text-xs font-normal text-slate-400">({accounts.length})</span>
        </span>
        <span className="text-sm font-semibold text-slate-700 tabular-nums">{formatCurrency(total)}</span>
      </button>

      {isOpen && (
        <div className="space-y-0.5 pb-1">
          {accounts.map(a => (
            <AccountRow key={a.accountId} account={a} onSelectAccount={onSelectAccount} />
          ))}
        </div>
      )}
    </div>
  );
};

const GroupCard = ({ groupKey, title, accounts, openKeys, toggle, onSelectAccount }) => {
  const isOpen = openKeys.has(groupKey);
  const total = sumCosts(accounts);
  const isCore = groupKey === "core";

  const prodAccounts = accounts.filter(a => a.environment === "prod");
  const devAccounts = accounts.filter(a => a.environment === "dev");
  const otherAccounts = accounts.filter(a => a.environment !== "prod" && a.environment !== "dev");

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => toggle(groupKey)}
        title={`${title} — total for selected range: ${formatCurrency(total)}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <span className="flex items-center gap-2 font-semibold text-slate-800">
          <ChevronToggle open={isOpen} />
          {title}
          <span className="text-xs font-normal text-slate-400">
            ({accounts.length} account{accounts.length === 1 ? "" : "s"})
          </span>
        </span>
        <span className="text-base font-bold text-brand-dark tabular-nums">{formatCurrency(total)}</span>
      </button>

      {isOpen && (
        <div className="px-3 py-2 bg-white">
          {isCore ? (
            <div className="space-y-0.5">
              {accounts.map(a => (
                <AccountRow key={a.accountId} account={a} onSelectAccount={onSelectAccount} />
              ))}
            </div>
          ) : (
            <>
              <EnvironmentBucket
                label="Production"
                accounts={prodAccounts}
                path={`${groupKey}:prod`}
                openKeys={openKeys}
                toggle={toggle}
                onSelectAccount={onSelectAccount}
              />
              <EnvironmentBucket
                label="Development"
                accounts={devAccounts}
                path={`${groupKey}:dev`}
                openKeys={openKeys}
                toggle={toggle}
                onSelectAccount={onSelectAccount}
              />
              <EnvironmentBucket
                label="Other"
                accounts={otherAccounts}
                path={`${groupKey}:other`}
                openKeys={openKeys}
                toggle={toggle}
                onSelectAccount={onSelectAccount}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Organization-chart-style view of accounts: Group (AMS/VMS/DPR OMS-EPC/
// RE-Vortex/Core) -> Production/Development -> individual account, each
// level independently collapsible. Only ever renders groups that actually
// have accounts present in `data.breakdown` — since the backend already
// scopes that list per user (see aws-cost-api's config/teams.js), a
// team_member automatically only ever sees their own team's branch here.
const AccountOrgTree = ({ data, loading, onSelectAccount }) => {
  const [openKeys, setOpenKeys] = useState(new Set());
  const didInitRef = useRef(false);

  const byGroup = useMemo(() => {
    const map = new Map();
    if (!data) return map;

    data.breakdown.forEach(account => {
      const key = getAccountGroupKey(account.accountId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(account);
    });

    return map;
  }, [data]);

  const visibleGroups = useMemo(
    () => GROUP_ORDER.filter(key => byGroup.has(key)),
    [byGroup]
  );

  // Default every product-team group card open (so its Production/
  // Development subtotals are visible, though the individual account rows
  // inside those still stay collapsed until clicked) — except Core
  // Accounts, which starts collapsed like everything used to. Applies to
  // both admin and team-scoped logins. Only happens once: later data
  // refreshes (e.g. changing the date range) don't stomp on whatever the
  // user has since collapsed/expanded by hand.
  useEffect(() => {
    if (didInitRef.current || !visibleGroups.length) return;
    didInitRef.current = true;

    setOpenKeys(new Set(visibleGroups.filter(key => key !== "core")));
  }, [visibleGroups]);

  const toggle = (key) => {
    setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonGroup key={i} />)}
      </div>
    );
  }

  if (!data) {
    return <p className="text-slate-400 text-sm py-8 text-center">No data available for this period.</p>;
  }

  if (!visibleGroups.length) {
    return <p className="text-slate-400 text-sm py-8 text-center">No accounts available for this period.</p>;
  }

  return (
    <div className="space-y-3">
      {visibleGroups.map(key => (
        <GroupCard
          key={key}
          groupKey={key}
          title={GROUPS[key].title}
          accounts={byGroup.get(key)}
          openKeys={openKeys}
          toggle={toggle}
          onSelectAccount={onSelectAccount}
        />
      ))}
    </div>
  );
};

export default AccountOrgTree;
